import React from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';

import { businessMonth, shiftMonth } from 'config/time';

/**
 * `YYYY-MM` を画面の言語の年月にする（日本語「2026年11月」、英語「November 2026」）。
 * 月の名前は Intl で作る（暦の月なので UTC で読む）。
 */
export function formatMonthLabel(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  if (language === 'jp') return `${year}年${monthNumber}月`;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, monthNumber - 1, 1))
  );
}

interface MonthNavigatorProps {
  /** 表示している月（`YYYY-MM`）。 */
  month: string;
  onChange: (month: string) => void;
  className?: string;
}

/**
 * A28・A36（2026-11-03 和田様の UAT）。履歴の一覧の月の切り替え。
 *
 * カレンダーのアプリのように、表の外の見出しに「◀ 2026年11月 ▶」と「今月」を置く。表の中は
 * 選んだ月の記録をページ送りで並べる（月の範囲は呼び出し側が monthBounds で作る）。
 * 「今月」は業務時間帯（会社の時間帯）の今の月。今の月を表示中なら押せない。
 */
const MonthNavigator: React.FC<MonthNavigatorProps> = ({ month, onChange, className = '' }) => {
  const { t, i18n } = useTranslation('common');
  const current = businessMonth();
  const label = formatMonthLabel(month, i18n.language);

  return (
    <div
      role="group"
      aria-label={t('month-nav.label')}
      className={`flex flex-row items-center gap-1 ${className}`}
      data-month={month}
    >
      <button
        type="button"
        className="size-9 border rounded-md flex items-center justify-center text-primary/300"
        onClick={() => onChange(shiftMonth(month, -1))}
        aria-label={t('month-nav.previous')}
      >
        <RiArrowLeftSLine aria-hidden="true" />
      </button>
      {/* 月を替えたら読み上げで今の月を伝える。 */}
      <p
        className="min-w-[8.5rem] text-center text-subtitle-1 font-medium text-primary/400 whitespace-nowrap"
        aria-live="polite"
        data-testid="month-nav-label"
      >
        {label}
      </p>
      <button
        type="button"
        className="size-9 border rounded-md flex items-center justify-center text-primary/300"
        onClick={() => onChange(shiftMonth(month, 1))}
        aria-label={t('month-nav.next')}
      >
        <RiArrowRightSLine aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`ml-1 px-3 h-9 border rounded-md whitespace-nowrap ${
          month === current ? 'text-neutral/75' : 'text-primary/300'
        }`}
        onClick={() => onChange(current)}
        disabled={month === current}
      >
        {t('month-nav.current')}
      </button>
    </div>
  );
};

export default MonthNavigator;
